<template>
    <div class="container-fluid job-container">
        <div
            class="card job-details-card"
            v-if="!loading && job"
        >
            <div class="card-body">
                <h4>{{ job.name }}</h4>
                <p
                    v-if="updatedAt"
                    class="job-updated-at"
                >
                    <strong>Last Updated:</strong> {{ updatedAt }}
                </p>
                <p>
                    <strong>Current Status:</strong>
                    <span>{{ job.status | capitalize }}</span>
                </p>
                <p v-if="job.first_name">
                    <strong>First name:</strong>
                    <span>{{ job.first_name }}</span>
                </p>
                <p v-if="job.last_name">
                    <strong>Last name:</strong>
                    <span>{{ job.last_name }}</span>
                </p>
                <div
                    class="job-important"
                    v-if="importantInfo"
                >
                    <strong><i class="fas fa-exclamation-triangle" /> Important Info:</strong>
                    <div v-html="importantInfo" />
                </div>
                <strong>Description:</strong>
                <div
                    class="job-decription"
                    v-html="description"
                />
                <hr>
                <div>
                    <p v-if="job.created_by_user && job.created_at">
                        Created by {{ job.created_by_user.name }} at {{ job.created_at.format(config.format) }}
                    </p>
                    <p v-if="job.updated_by_user && job.updated_at">
                        Last updated by {{ job.updated_by_user.name }} at {{ job.updated_at.format(config.format) }}
                    </p>
                </div>
            </div>
        </div>

        <div
            class="card job-times-card"
            v-if="!loading && job.time_slots"
        >
            <div class="card-body">
                <div class="job-times-title">
                    <h5>Staff and Times</h5>
                    <span class="job-current-time">Current time: {{ now.format('hh:mm a') }}</span>
                </div>
                <ul class="job-times-list">
                    <li
                        v-for="(timeSlot) of job.time_slots"
                        :key="timeSlot.id"
                        class="times-list-item"
                        :class="{passed: timeSlot.end < now }"
                    >
                        <div
                            class="times-list-item-user"
                            :class="timeSlot.user.id === user.id ? 'times-list-item-current-user' : ''"
                        >
                            <i class="fa fa-user" />{{ timeSlot.user.name }}
                        </div>
                        <div class="times-list-item-start">
                            {{ timeSlot.start.format('ddd Do MMM') }}
                        </div>
                        <i class="times-list-item-sep fa fa-angle-double-right" />
                        <div class="times-list-item-end">
                            {{ timeSlot.end.format('ddd Do MMM') }}
                        </div>
                    </li>
                </ul>
                <div class="row job-times-buttons">
                    <div class="col-12 text-center">
                        <button
                            class="btn btn-primary"
                            v-if="job.status !== 'attended' && job.status !== 'invoiced'"
                            @click="jobAttended()"
                        >
                            Job Has Been Attended <i class="fa fa-check" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="card forms-times-card"
            v-if="!loading && job.forms"
        >
            <div class="card-body">
                <h5>Forms</h5>
                <ul class="job-forms-list">
                    <li
                        v-for="(form, index) of job.forms.filter(f => !f.type.toLowerCase().includes('certificate'))"
                        :key="index"
                        class="job-forms-item"
                    >
                        <router-link :to="{name: 'engineer.forms.edit', params: {id: form.id}}">
                            {{ form.name }}
                            <i class="fa fa-pencil-alt" />
                        </router-link>
                    </li>
                </ul>
                <button
                    class="btn btn-primary"
                    @click="newForm()"
                >
                    <i class="fa fa-plus" /> Add Form
                </button>
            </div>
        </div>

        <div
            class="card forms-times-card"
            v-if="!loading && job.forms"
        >
            <div class="card-body">
                <h5>Service Inventory</h5>
                <button
                    v-if="!showInventory"
                    class="btn btn-sm btn-info"
                    @click.prevent="showInventory = true"
                >
                    Show Inventory
                </button>
                <button
                    v-if="showInventory"
                    class="btn btn-sm btn-info"
                    @click.prevent="showInventory = false"
                >
                    Hide Inventory
                </button>
                <job-inventory
                    v-if="showInventory"
                    :job="job"
                />
            </div>
        </div>

        <div
            class="card job-site-card"
            v-if="!loading && job.site"
        >
            <div class="card-body">
                <h5>Site</h5>
                <a
                    href="#"
                    name="site"
                />
                <div
                    class="job-map"
                    id="job-map"
                />
                <h6>#{{ job.site.site_no }} {{ job.site.name }}</h6>
                <ul class="address-list">
                    <li
                        v-for="(line, index) of address"
                        :key="index"
                    >
                        {{ line }}
                    </li>
                </ul>
                <a
                    v-if="directionsUrl"
                    :href="directionsUrl"
                    class="btn btn-primary"
                    target="_blank"
                >
                    <i class="fa fa-location-arrow" /> Get Directions
                </a>
                <a
                    v-if="w3wUrl"
                    :href="w3wUrl"
                    class="btn btn-primary"
                    target="_blank"
                >
                    <i class="fa fa-location-arrow" /> View on What3Words
                </a>
                <hr>
                <p v-if="job.site.contact_name">
                    Contact name: {{ job.site.contact_name }}
                </p>
                <p v-if="job.site.telephone">
                    Telephone: <a :href="'tel:' + job.site.telephone">{{ job.site.telephone }}</a>
                </p>
                <p v-if="job.site.mobile_number">
                    Mobile: <a :href="'tel:' + job.site.mobile_number">{{ job.site.mobile_number }}</a>
                </p>
                <p v-if="job.site.email">
                    Email: <a :href="'mailto:' + job.site.email">{{ job.site.email }}</a>
                </p>
            </div>
        </div>

        <div
            class="card job-customer-card"
            v-if="!loading && job.customer"
        >
            <div class="card-body">
                <h5>Customer</h5>
                <a
                    href="#"
                    name="customer"
                />
                <p>Company: {{ job.customer.company_name }}</p>
                <p v-if="job.customer.billing_telephone">
                    Telephone: <a :href="'tel:' + job.customer.billing_telephone">{{ job.customer.billing_telephone
                    }}</a>
                </p>
                <p v-if="job.customer.billing_email">
                    Email: <a :href="'mailto:' + job.customer.billing_email">{{ job.customer.billing_email }}</a>
                </p>

                <p v-if="job.customer.responsible_person_name">
                    Responsible Person: {{ job.customer.responsible_person_name }}
                </p>

                <p v-if="job.customer.responsible_person_telephone">
                    Responsible Person Telephone: <a :href="'tel:' + job.customer.responsible_person_telephone">{{ job.customer.responsible_person_telephone
                    }}</a>
                </p>
                <p v-if="job.customer.responsible_person_email">
                    Responsible Person Email: <a :href="'mailto:' + job.customer.responsible_person_email">{{ job.customer.responsible_person_email }}</a>
                </p>

                <router-link
                    :to="{ name: routeBase+'.customers.show', params: { id: job.customer_id } }"
                    class="btn btn-primary"
                >
                    View Customer
                </router-link>
            </div>
        </div>

        <div
            class="card job-customer-card"
            v-if="!loading && job.customer"
        >
            <div class="card-body">
                <h5>Notes</h5>
                <comments :lock-site="true" />
            </div>
        </div>

        <div
            class="card"
            v-if="job.id"
        >
            <div class="card-body">
                <h5>Attachments</h5>
                <attachments
                    v-if="job.id"
                    type="App\Job"
                    :id="job.id"
                />
            </div>
        </div>

        <nav class="job-bottom-nav">
            <router-link
                to="/engineer"
                class="btn btn-info"
            >
                <i class="fa fa-chevron-left" />
                <span class="nav-text">Jobs</span>
            </router-link>
            <a
                href="#site"
                class="btn btn-info btn-middle"
            >
                <i class="fa fa-location-arrow" />
                <span class="nav-text">Site</span>
            </a>
            <a
                href="#customer"
                class="btn btn-info"
            >
                <i class="fa fa-building" />
                <span class="nav-text">Customer</span>
            </a>
        </nav>

        <span v-if="loading">Loading
            <i class="fa fa-spin fa-spinner" />
        </span>

        <new-form-modal
            ref="newFormModal"
            :params="{job_id: job.id, customer_id: job.customer_id}"
            route="engineer"
        />
    </div>
</template>

<script>
import JobService from "../../services/JobService";
import AuthService from "../../services/AuthService";
import NewFormModal from "../forms/NewFormModal.vue";
import RouterBase from "../../mixins/RouterBaseMixin";
import Comments from "../CommentsComponent.vue";
import JobInventory from "./JobInventoryComponent";
import UsesComments from '../../mixins/UsesComments';
import Attachments from '../AttachmentsComponent';

export default {
    components: {
        "new-form-modal": NewFormModal,
        comments: Comments,
        JobInventory,
        Attachments,
    },
    mixins: [
        RouterBase,
        UsesComments,
    ],
    props: {
        id: {
            required: true,
            type: [Number,String],
        }
    },
    data: function() {
        return {
            job: {},
            loading: false,
            map: null,
            user: {},
            now: window.moment(),
            timer: null,
            config: {
                format: "DD/MM/YYYY HH:mm"
            },
            showInventory: false
        };
    },

    computed: {
        address: function() {
            const address = [];
            const keys = [
                "address_line_1",
                "address_line_2",
                "address_line_3",
                "town",
                "region",
                "postcode",
                "country"
            ];
            if (this.job.site.address) {
                const a = this.job.site.address;
                for (let k of keys) {
                    if (a[k]) {
                        address.push(a[k]);
                    }
                }
            }
            return address;
        },

        directionsUrl: function() {
            if (!this.job.site.address.lat || !this.job.site.address.lng) {
                return null;
            }
            return encodeURI(
                [
                    "https://www.google.com/maps/dir/?api=1",
                    "destination=" +
                        this.job.site.address.lat +
                        "," +
                        this.job.site.address.lng,
                    "travelmode=driving"
                ].join("&")
            );
        },

        w3wUrl: function() {
            if (!this.job.site.address.w3w) {
                return null;
            }
            return "https://what3words.com/" + this.job.site.address.w3w;
        },

        updatedAt() {
            if (!this.job || !this.job.updated_at) {
                return null;
            }
            return this.job.updated_at.fromNow();
        },

        usersTimeSlots () {
            return this.job.time_slots.filter(ts => ts.user_id === this.user.id);
        },

        description () {
            if (!this.job.description) {
                return 'No description';
            }
            return window.injectTelAndEmails(this.job.description);
        },
        importantInfo () {
            if (!this.job.important_info) {
                return null;
            }
            return window.injectTelAndEmails(this.job.important_info);
        },
    },

    watch: {
        $route() {
            this.loadJob();
        }
    },

    mounted: function() {
        AuthService.getUser().then(user => {
            this.user = user;
        });
        this.loadJob();
        this.timer = setInterval(() => {
            this.now = window.moment();
        }, 100);
    },

    beforeDestroy: function() {
        clearInterval(this.timer);
        this.unsubscribe();
    },

    methods: {

        loadJob: function() {
            this.unsubscribe();
            this.loading = true;
            JobService.get(this.id).then(
                res => {
                    this.job = res.data;
                    this.setCommentsParams({
                        customerId: this.job.customer_id,
                        siteId: this.job.site_id,
                    });
                    this.loading = false;
                    this.initMap();
                    this.subscribe();
                },
                err => {
                    alert("Error: Failed to load job");
                    throw err;
                }
            );
        },

        initMap: function() {
            const site = {
                lat: this.job.site.address.lat,
                lng: this.job.site.address.lng
            };
            if (site.lat === null || site.lng === null) {
                return;
            }
            setTimeout(() => {
                this.map = new window.google.maps.Map(
                    document.getElementById("job-map"),
                    {
                        zoom: 16,
                        center: site
                    }
                );
                new window.google.maps.Marker({
                    position: site,
                    map: this.map
                });
            }, 500);
        },

        jobAttended: function() {
            this.loading = true;
            JobService.attended(this.job).then(
                () => {
                    this.loadJob();
                    alert("Job status updated");
                    this.initMap();
                },
                () => {
                    alert("Error: Failed to update the job");
                }
            );
        },

        newForm: function() {
            this.$refs.newFormModal.show();
        },

        subscribe() {
            window.Echo.channel("jobs").listen("JobUpdated", e => {
                if (this.job.id == e.job_id) {
                    this.loadJob();
                }
            });
        },

        unsubscribe() {
            window.Echo.leave("jobs");
        }
    }
};
</script>




// WEBPACK FOOTER //
// resources/assets/js/components/engineer/JobComponent.vue